// Phase 1.5 item #2D · Annotation queue
//
// Backend routes:
//   GET    /api/observability/annotations[?status=pending|completed]
//   POST   /api/observability/annotations/{id}/complete  (admin)
//
// Pending state on a fresh Langfuse cluster: backend may HTTP 500
// when no annotation queue exists yet. Handled gracefully — render
// empty state, not a red error.
import React, { useEffect, useState } from "react";
import { COLORS } from "../../../theme/colors";
import { Button, Badge, Modal, TextArea, Select } from "../../../components/ui";
import { observabilityApi } from "../../../api/client";
import { useAuth } from "../../../contexts/AuthContext";
import { EmptyState, ErrorState, LoadingRow, TableHeader, TableRow, StatePill } from "./SharedUI";
import { humanizeError as _humanizeError } from "../../../utils/humanizeError";

// MNT-014: shared humanizer + this page's status-specific overrides.
const humanizeError = (e) => _humanizeError(e, { 403: "Complete requires administrator role." });

const ADMIN_ROLES = ["owner", "administrator"];

export function AnnotationQueuePage() {
  const { currentUser } = useAuth();
  const canComplete = ADMIN_ROLES.includes(currentUser?.role || "");
  const [annotations, setAnnotations] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [completing, setCompleting] = useState(null);  // annotation_id of row being completed
  const [verdict, setVerdict] = useState("ACCURATE");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const res = await observabilityApi.listAnnotations(filter);
      if (res?.success === false) {
        // HTTP 500 on no-queue case → render as empty state, not error
        const msg = res.error || "Failed to load";
        if (/no queue|not.*provisioned|temporarily/i.test(msg)) {
          setAnnotations([]);
        } else {
          setError(msg);
        }
      } else {
        setAnnotations(res?.data || res || []);
      }
    } catch (e) {
      if (e?.status === 500) {
        // Treat 500 as empty (no queue yet) — see backend gap §8.4
        setAnnotations([]);
      } else {
        setError(humanizeError(e));
        setAnnotations([]);
      }
    }
  };
  useEffect(() => { load(); }, [filter]);

  const onComplete = async () => {
    if (!completing) return;
    setBusy(true);
    try {
      const res = await observabilityApi.completeAnnotation(completing, verdict, notes || null);
      if (res?.success === false) {
        setError(res.error || "Complete failed");
      } else {
        setCompleting(null); setNotes(""); setVerdict("ACCURATE");
        await load();
      }
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  if (annotations === null && !error) return <LoadingRow label="Loading annotation queue…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  const items = Array.isArray(annotations) ? annotations : (annotations?.data || []);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: COLORS.textMuted }}>
          {items.length} {filter} item{items.length === 1 ? "" : "s"}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {["pending", "completed"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 4,
                cursor: "pointer",
                background: filter === f
                  ? (COLORS.accentSoft || COLORS.surface)
                  : "transparent",
                color: filter === f ? (COLORS.accentText || COLORS.accent) : COLORS.textMuted,
                border: `1px solid ${filter === f ? (COLORS.accent || COLORS.border) : COLORS.border}`,
              }}
            >{f}</button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title={filter === "pending" ? "No pending annotations" : "No completed annotations yet"}
          detail={filter === "pending"
            ? "Annotation queues hold conversation turns flagged for human review. Configure a queue in Langfuse (internal eng) and flag turns from the Inspector."
            : "Once you complete pending items, they appear here."}
        />
      ) : (
        <div>
          <TableHeader cols={["TRACE", "QUEUE", "ASSIGNED", "STATUS", "ACTIONS"]} />
          {items.map(a => (
            <TableRow
              key={a.id || a.annotation_id}
              cells={[
                <span key="t" style={{ fontFamily: "monospace", fontSize: 11 }}>
                  {(a.trace_id || a.traceId || "").slice(0, 16)}…
                </span>,
                <Badge key="q" color={COLORS.textMuted}>{a.queue_id || a.queueId || "—"}</Badge>,
                <span key="a" style={{ fontSize: 11, color: COLORS.textMuted }}>
                  {a.assignee || a.assignedTo || "unassigned"}
                </span>,
                <StatePill key="s" state={a.status || "pending"} />,
                canComplete && filter === "pending" ? (
                  <Button
                    key="b" size="sm" variant="secondary"
                    onClick={() => setCompleting(a.id || a.annotation_id)}
                  >Complete</Button>
                ) : <span key="b" />,
              ]}
            />
          ))}
        </div>
      )}

      <Modal
        open={Boolean(completing)}
        onClose={() => setCompleting(null)}
        title="Complete annotation"
        width={500}
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="secondary" onClick={() => setCompleting(null)}>Cancel</Button>
            <Button onClick={onComplete} disabled={busy} loading={busy}>Submit</Button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Select label="Verdict" value={verdict} onChange={e => setVerdict(e.target.value)}>
            <option value="ACCURATE">ACCURATE</option>
            <option value="PARTIALLY_ACCURATE">PARTIALLY_ACCURATE</option>
            <option value="INACCURATE">INACCURATE</option>
          </Select>
          <TextArea
            label="Notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="What did you observe? Specifics help fine-tuning."
            rows={6}
          />
        </div>
      </Modal>
    </div>
  );
}

